import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const KEY = "mm_lang";
const LangCtx = createContext(null);

const DICT = {
  sv: {
    // Nav
    "nav.home": "Översikt",
    "nav.cases": "Fall",
    "nav.dx": "Diagnos",
    "nav.flash": "Kort",
    "nav.quiz": "Quiz",
    "nav.chat": "AI",
    "nav.fav": "Favoriter",
    "nav.forum": "Forum",
    "auth.login": "Logga in",
    "auth.logout": "Logga ut",
    "app.title": "Manuell Medicin",
    "app.subtitle": "Klinisk undersökning",
    "footer.text": "© Manuell Medicin · Utbildningsverktyg, ersätter inte klinisk bedömning.",
    "footer.support": "Stötta projektet",

    // Contact
    "contact.open": "Kontakta oss",
    "contact.title": "Kontakta oss",
    "contact.about": "Terapeutportalen.com är ett projekt där vi försöker underlätta klinisk undersökning av patienter. Portalens syfte är att lära sig mer om hur man undersöker patienter — för studenter, kliniker och alla som arbetar manuellt.",
    "contact.name": "Namn",
    "contact.email": "E-post",
    "contact.message": "Meddelande",
    "contact.send": "Skicka meddelande",
    "contact.privacy": "Din e-post används endast för att svara på ditt meddelande.",
    "contact.fillAll": "Fyll i namn, e-post och meddelande (minst 4 tecken).",
    "contact.errSend": "Kunde inte skicka — försök igen om en stund.",
    "contact.tooMany": "För många meddelanden från denna IP. Försök igen senare.",
    "contact.doneTitle": "Tack för ditt meddelande!",
    "contact.doneLead": "Vi återkommer så snart vi kan.",
    "common.close": "Stäng",
    "common.open": "Öppna",
    "sub.pickCategory": "Välj kategori",
    "sub.backToCategories": "Tillbaka till kategorier",
    "sub.reflexer": "Reflexer",
    "sub.reflexer.desc": "Senreflexer (biceps, triceps, patellar, achilles m.fl.) och abdominalreflex.",
    "sub.dermatom": "Dermatom",
    "sub.dermatom.desc": "Hudinnervation per nervrot — undersök sensorik nivå för nivå.",
    "sub.myotom": "Myotom",
    "sub.myotom.desc": "Muskelinnervering per nervrot — undersök motorisk styrka nivå för nivå.",

    // Home
    "home.eyebrow": "Klinisk undersökningsmetodik",
    "home.hero1": "Från",
    "home.heroAccent": "huvud till häl",
    "home.hero2": "— en strukturerad referens för manuell medicin.",
    "home.lead": "Bläddra bland kliniska tester per kroppsregion, träna med quiz och få stöd från en AI-assistent som hjälper dig tolka positiva fynd och föreslå differentialdiagnoser.",
    "home.ctaCases": "Träna kliniskt fall",
    "home.ctaDx": "Differentialdiagnos",
    "home.ctaFlash": "Flashcards",
    "home.statRegions": "Regioner",
    "home.statTests": "Tester",
    "home.chooseRegion": "Välj kroppsregion",
    "home.trainQuiz": "Träna med quiz",
    "home.openRegion": "Öppna region",
    "home.testsCount": "tester",
    "home.methodologyEyebrow": "Metodik",
    "home.methodologyTitle": "Stående → sittande → liggande.",
    "home.methodologyText1": "Undersökningen följer en strukturerad ordning. För varje region presenteras inspektion, aktiva rörelser, palpation och provokationstester med tydliga utförande och positiva fynd.",
    "home.methodologyText2": "Kombinera fynd från flera tester för att stärka eller försvaga din kliniska hypotes — och använd AI-assistenten för att resonera om differentialdiagnoser i realtid.",
    "home.errLoad": "Kunde inte ladda regioner",

    // Common
    "common.search": "Sök test eller syfte…",
    "common.loading": "Laddar…",
    "common.back": "Tillbaka",
    "common.backToOverview": "Tillbaka till översikt",
    "common.position": "Position",
    "common.tests": "tester",
    "common.region": "Region",
    "common.regions": "regioner",
    "common.preparing": "Förbereder…",
    "common.theme.toLight": "Ljust läge",
    "common.theme.toDark": "Mörkt läge",
    "common.lang": "EN",

    // Position values
    "pos.all": "Alla",
    "pos.Stående": "Stående",
    "pos.Sittande": "Sittande",
    "pos.Ryggliggande": "Ryggliggande",
    "pos.Magliggande": "Magliggande",
    "pos.Sidliggande": "Sidliggande",

    // Test dialog
    "test.purpose": "Syfte",
    "test.execution": "Utförande",
    "test.positive": "Positivt fynd",
    "test.comment": "Kommentar",
    "test.video": "Demonstration",
    "test.sens": "Sensitivitet:",
    "test.spec": "Specificitet:",
    "test.source": "Källa:",
    "test.lrPosTip": "Likelihood ratio positiv – >10 talar starkt för diagnosen",
    "test.lrNegTip": "Likelihood ratio negativ – <0.1 utesluter diagnosen starkt",
    "lr.title": "Tolka likelihood ratios",
    "lr.lead": "LR beskriver hur mycket testets resultat förändrar sannolikheten för diagnosen.",
    "lr.ruleIn": "Sannolikhet diagnos",
    "lr.ruleOut": "Sannolikhet utesluten",
    "lr.strong": "Stor förändring",
    "lr.moderate": "Måttlig förändring",
    "lr.weak": "Liten förändring",
    "lr.minimal": "Knappt någon förändring",
    "lr.cite": "Tröskelvärden enligt Jaeschke et al. JAMA 1994.",
    "test.share": "Kopiera länk till testet",
    "test.shareCopied": "Länk kopierad",
    "test.shareFail": "Kunde inte kopiera länk",
    "region.noResults": "Inga tester matchar filtren.",
    "region.errLoad": "Kunde inte ladda region",

    // Quiz
    "quiz.eyebrow": "Träning",
    "quiz.title": "Quiz",
    "quiz.lead": "Testa dina kunskaper. Para ihop syfte med rätt undersökningstest.",
    "quiz.region": "Region",
    "quiz.count": "Antal frågor",
    "quiz.allRegions": "Alla regioner",
    "quiz.start": "Starta quiz",
    "quiz.questionOf": "Fråga",
    "quiz.of": "/",
    "quiz.reveal": "Visa svar",
    "quiz.next": "Nästa fråga",
    "quiz.finish": "Avsluta",
    "quiz.explanation": "Förklaring",
    "quiz.correctText": "Du fick {score} av {total} rätt.",
    "quiz.excellent": "Utmärkt klinisk insikt!",
    "quiz.solid": "Solid grund — fortsätt öva.",
    "quiz.repeat": "Repetera och försök igen.",
    "quiz.new": "Nytt quiz",
    "quiz.selectFirst": "Välj ett svar först",
    "quiz.startFail": "Kunde inte starta quiz",

    // Cases
    "cases.eyebrow": "OSCE-träning",
    "cases.title1": "Klinisk fall",
    "cases.titleAccent": "– AI-bedömt.",
    "cases.lead": "AI:n genererar ett realistiskt patientfall. Du väljer vilka 3-5 tester du skulle utföra och får återkoppling om dina kliniska resonemang.",
    "cases.region": "Region",
    "cases.random": "Slumpmässig",
    "cases.difficulty": "Svårighetsgrad",
    "cases.diff.easy": "Lätt – klassisk presentation",
    "cases.diff.medium": "Medel – realistisk komplexitet",
    "cases.diff.hard": "Svår – atypisk / kombinerad",
    "cases.generate": "Generera nytt fall",
    "cases.case": "Patientfall",
    "cases.selected": "Dina valda tester",
    "cases.selectHint": "Välj 3-5 tester du skulle utföra. AI:n bedömer dina val.",
    "cases.searchTest": "Sök test…",
    "cases.submit": "Skicka in & få bedömning",
    "cases.new": "Nytt fall",
    "cases.assessment": "Bedömning · Claude Sonnet 4.5",
    "cases.scoreExcellent": "Utmärkt klinisk bedömning",
    "cases.scoreSolid": "Solid grund, kan finslipas",
    "cases.scoreRepeat": "Repetera och försök igen",
    "cases.backToCase": "Tillbaka till fallet",
    "cases.errLoad": "Kunde inte ladda data",
    "cases.errGen": "Kunde inte generera fall",
    "cases.errAssess": "Kunde inte bedöma val",
    "cases.selectOne": "Välj minst ett test",

    // Chat
    "chat.eyebrow": "Claude Sonnet 4.5",
    "chat.title": "AI-assistent",
    "chat.lead": "Klinisk resonans, differentialdiagnoser och teststöd på svenska.",
    "chat.suggestionsTitle": "Förslag att börja med:",
    "chat.sug1": "Vad innebär ett positivt Spurling-test och vilka strukturer kan vara involverade?",
    "chat.sug2": "Min patient har positivt Lachman och negativt anterior drawer – hur tolkar du det?",
    "chat.sug3": "Skillnad mellan Hawkins-Kennedy och Neer impingement?",
    "chat.sug4": "Vilka tester ska jag göra först vid misstänkt cauda equina?",
    "chat.input": "Skriv din kliniska fråga…",
    "chat.thinking": "Tänker…",
    "chat.disclaimer": "AI-genererat innehåll — verifiera kliniskt. Ersätter inte läkarbedömning.",
    "chat.reset": "Nollställ",
    "chat.err": "AI-assistenten kunde inte svara just nu",

    // Diagnose
    "dx.eyebrow": "Klinisk resonans",
    "dx.title1": "Differentialdiagnos",
    "dx.titleAccent": "– byggd från fynd.",
    "dx.lead": "Markera de tester som var positiva hos din patient. AI:n (Claude Sonnet 4.5) sammanfattar troliga diagnoser, föreslår följdtester och red flags att utesluta.",
    "dx.search": "Sök test eller region…",
    "dx.importFav": "Importera {n} favoriter",
    "dx.selected": "Valda positiva tester",
    "dx.empty": "Markera tester till vänster, eller importera från favoriter.",
    "dx.notes": "Anamnes / fritext (valfritt)",
    "dx.notesPh": "Ex: 32-årig löpare, 3 v insidesknäsmärta efter ökad träning…",
    "dx.run": "Generera differentialdiagnos",
    "dx.result": "AI-analys (Claude Sonnet 4.5)",
    "dx.disclaimer": "Bedömningen är AI-genererad och ersätter inte klinisk undersökning.",
    "dx.howTitle": "Hur det fungerar",
    "dx.how": "AI:n kombinerar dina positiva fynd med klinisk evidens från undersökningsmetodiken och ger en rangordnad differentialdiagnos. Den föreslår också relevanta följdtester och red flags.",
    "dx.discuss": "Behöver du diskutera ett fall? Öppna AI-assistenten",
    "dx.errLoad": "Kunde inte hämta tester",
    "dx.errRun": "AI-tjänsten är inte tillgänglig just nu",
    "dx.selectFirst": "Markera minst ett positivt test",
    "dx.noFav": "Inga favoriter att importera",
    "dx.added": "{n} favoriter tillagda",

    // Flashcards
    "flash.eyebrow": "Snabbrepetition",
    "flash.title": "Flashcards",
    "flash.lead": "Vänd på korten för att se utförande och positivt fynd. Markera \"Kunde\" eller \"Behöver öva\".",
    "flash.region": "Region",
    "flash.count": "Antal kort",
    "flash.allRegions": "Alla regioner",
    "flash.start": "Starta flashcards",
    "flash.cardOf": "Kort",
    "flash.knewCount": "{n} kunde",
    "flash.reviewCount": "{n} att repetera",
    "flash.tapToReveal": "Tryck för att se svar →",
    "flash.answer": "Svar",
    "flash.needPractice": "Behöver öva",
    "flash.knew": "Kunde",
    "flash.summary": "{known} kunde · {review} att repetera · {total} totalt",
    "flash.reviewThese": "Repetera dessa",
    "flash.newRound": "Ny omgång",
    "flash.redoReview": "Repetera {n} kort",
    "flash.noneAvail": "Inga kort tillgängliga",
    "flash.errLoad": "Kunde inte ladda kort",

    // Favorites
    "fav.eyebrow": "Sparat",
    "fav.title": "Favoriter",
    "fav.lead1": "Snabbåtkomst till tester du markerat. Spara med",
    "fav.lead2": "på vilket test som helst.",
    "fav.clearAll": "Rensa alla",
    "fav.cleared": "Favoriter rensade",
    "fav.emptyTitle": "Inga favoriter än",
    "fav.emptyLead": "Bläddra bland regioner och spara tester du vill kunna nå snabbt.",
    "fav.explore": "Utforska regioner",
    "fav.countLabel": "{n} favorittester",
    "fav.useAsDx": "Använd som positiva fynd i AI-diagnosen",
    "fav.toDx": "Skicka till differentialdiagnos",
    "fav.errLoad": "Kunde inte ladda favoriter",

    // About
    "about.eyebrow": "Om appen",
    "about.title1": "Klinisk undersökningsmetodik",
    "about.titleAccent": "– digitaliserad.",
    "about.lead": "Appen sammanfattar metodiken för manuell medicin från huvud till häl. Tester per region, med fokus på syfte, utförande och positivt fynd. AI-assistenten hjälper dig tolka fynd och resonera om differentialdiagnoser.",
    "about.item1Title": "Strukturerad metodik",
    "about.item1Text": "Stående → sittande → liggande. Inspektion, aktiva rörelser, palpation och provokationstester per region.",
    "about.item2Title": "Klinisk referens",
    "about.item2Text": "Snabb sökning och filter på position. Idealt under praktiska tentor och i kliniken.",
    "about.item3Title": "Säkerhet",
    "about.item3Text": "Appen är ett utbildningsverktyg. Ersätter aldrig en riktig läkares bedömning – särskilt inte vid red flags.",
    "about.sourceLabel": "Källa",
    "about.sourceText": "Baserad på Klinisk undersökning (Magee-referenser).",

    // Support / Swish
    "sup.title": "Stötta projektet",
    "sup.lead": "Manuell Medicin är ett oberoende utbildningsverktyg. Varje bidrag hjälper till att täcka AI-kostnader och att hålla appen kostnadsfri för studenter.",
    "sup.otherAmount": "Eller annat belopp",
    "sup.placeholder": "t.ex. 150",
    "sup.scan": "Skanna QR-koden i Swish-appen",
    "sup.openApp": "Öppna i Swish-appen",
    "sup.swishLabel": "Swish-nr",
    "sup.thanks": "Tack för ditt bidrag! 💙",
    "sup.copied": "Swish-nummer kopierat",
    "sup.copyFail": "Kunde inte kopiera",
  },

  en: {
    // Nav
    "nav.home": "Overview",
    "nav.cases": "Cases",
    "nav.dx": "Diagnosis",
    "nav.flash": "Cards",
    "nav.quiz": "Quiz",
    "nav.chat": "AI",
    "nav.fav": "Favorites",
    "nav.forum": "Forum",
    "auth.login": "Log in",
    "auth.logout": "Log out",
    "app.title": "Manual Medicine",
    "app.subtitle": "Clinical examination",
    "footer.text": "© Manual Medicine · Educational tool, does not replace clinical judgment.",
    "footer.support": "Support the project",

    // Contact
    "contact.open": "Contact us",
    "contact.title": "Contact us",
    "contact.about": "Terapeutportalen.com is a project where we try to make clinical examination of patients easier. The portal's purpose is to help you learn more about how to examine patients — for students, clinicians and anyone working with manual medicine.",
    "contact.name": "Name",
    "contact.email": "Email",
    "contact.message": "Message",
    "contact.send": "Send message",
    "contact.privacy": "Your email is only used to reply to your message.",
    "contact.fillAll": "Fill in name, email and a message (at least 4 characters).",
    "contact.errSend": "Could not send – please try again shortly.",
    "contact.tooMany": "Too many messages from this IP. Please try again later.",
    "contact.doneTitle": "Thanks for your message!",
    "contact.doneLead": "We'll get back to you as soon as we can.",
    "common.close": "Close",
    "common.open": "Open",
    "sub.pickCategory": "Pick a category",
    "sub.backToCategories": "Back to categories",
    "sub.reflexer": "Reflexes",
    "sub.reflexer.desc": "Tendon reflexes (biceps, triceps, patellar, achilles, etc.) and abdominal reflex.",
    "sub.dermatom": "Dermatomes",
    "sub.dermatom.desc": "Cutaneous innervation by nerve root — assess sensation level by level.",
    "sub.myotom": "Myotomes",
    "sub.myotom.desc": "Muscle innervation by nerve root — test motor strength level by level.",

    // Home
    "home.eyebrow": "Clinical examination methodology",
    "home.hero1": "From",
    "home.heroAccent": "head to heel",
    "home.hero2": "— a structured reference for manual medicine.",
    "home.lead": "Browse clinical tests by body region, train with quizzes, and get support from an AI assistant that helps you interpret positive findings and suggest differential diagnoses.",
    "home.ctaCases": "Try a clinical case",
    "home.ctaDx": "Differential diagnosis",
    "home.ctaFlash": "Flashcards",
    "home.statRegions": "Regions",
    "home.statTests": "Tests",
    "home.chooseRegion": "Choose body region",
    "home.trainQuiz": "Train with quiz",
    "home.openRegion": "Open region",
    "home.testsCount": "tests",
    "home.methodologyEyebrow": "Methodology",
    "home.methodologyTitle": "Standing → seated → lying.",
    "home.methodologyText1": "The examination follows a structured order. For each region we present inspection, active range of motion, palpation, and provocation tests with clear execution and positive findings.",
    "home.methodologyText2": "Combine findings from multiple tests to strengthen or weaken your clinical hypothesis — and use the AI assistant to reason about differential diagnoses in real time.",
    "home.errLoad": "Couldn't load regions",

    // Common
    "common.search": "Search test or purpose…",
    "common.loading": "Loading…",
    "common.back": "Back",
    "common.backToOverview": "Back to overview",
    "common.position": "Position",
    "common.tests": "tests",
    "common.region": "Region",
    "common.regions": "regions",
    "common.preparing": "Preparing…",
    "common.theme.toLight": "Light mode",
    "common.theme.toDark": "Dark mode",
    "common.lang": "SV",

    // Position values (translate)
    "pos.all": "All",
    "pos.Stående": "Standing",
    "pos.Sittande": "Seated",
    "pos.Ryggliggande": "Supine",
    "pos.Magliggande": "Prone",
    "pos.Sidliggande": "Side-lying",

    // Test dialog
    "test.purpose": "Purpose",
    "test.execution": "Execution",
    "test.positive": "Positive finding",
    "test.comment": "Comment",
    "test.video": "Demonstration",
    "test.sens": "Sensitivity:",
    "test.spec": "Specificity:",
    "test.source": "Source:",
    "test.lrPosTip": "Likelihood ratio positive – >10 strongly suggests the diagnosis",
    "test.lrNegTip": "Likelihood ratio negative – <0.1 strongly rules out the diagnosis",
    "lr.title": "How to read likelihood ratios",
    "lr.lead": "LR tells you how much the test result shifts the probability of the diagnosis.",
    "lr.ruleIn": "Probability of disease",
    "lr.ruleOut": "Probability ruled out",
    "lr.strong": "Large shift",
    "lr.moderate": "Moderate shift",
    "lr.weak": "Small shift",
    "lr.minimal": "Barely any shift",
    "lr.cite": "Thresholds per Jaeschke et al. JAMA 1994.",
    "test.share": "Copy link to test",
    "test.shareCopied": "Link copied",
    "test.shareFail": "Couldn't copy link",
    "region.noResults": "No tests match the filters.",
    "region.errLoad": "Couldn't load region",

    // Quiz
    "quiz.eyebrow": "Training",
    "quiz.title": "Quiz",
    "quiz.lead": "Test your knowledge. Match the purpose with the correct examination test.",
    "quiz.region": "Region",
    "quiz.count": "Number of questions",
    "quiz.allRegions": "All regions",
    "quiz.start": "Start quiz",
    "quiz.questionOf": "Question",
    "quiz.of": "/",
    "quiz.reveal": "Reveal answer",
    "quiz.next": "Next question",
    "quiz.finish": "Finish",
    "quiz.explanation": "Explanation",
    "quiz.correctText": "You got {score} out of {total} correct.",
    "quiz.excellent": "Excellent clinical insight!",
    "quiz.solid": "Solid foundation — keep practicing.",
    "quiz.repeat": "Review and try again.",
    "quiz.new": "New quiz",
    "quiz.selectFirst": "Select an answer first",
    "quiz.startFail": "Couldn't start quiz",

    // Cases
    "cases.eyebrow": "OSCE training",
    "cases.title1": "Clinical case",
    "cases.titleAccent": "– AI-graded.",
    "cases.lead": "The AI generates a realistic patient case. You choose 3-5 tests you would perform and get feedback on your clinical reasoning.",
    "cases.region": "Region",
    "cases.random": "Random",
    "cases.difficulty": "Difficulty",
    "cases.diff.easy": "Easy – classic presentation",
    "cases.diff.medium": "Medium – realistic complexity",
    "cases.diff.hard": "Hard – atypical / combined",
    "cases.generate": "Generate new case",
    "cases.case": "Patient case",
    "cases.selected": "Your selected tests",
    "cases.selectHint": "Choose 3-5 tests you would perform. The AI will assess your choices.",
    "cases.searchTest": "Search test…",
    "cases.submit": "Submit & get assessment",
    "cases.new": "New case",
    "cases.assessment": "Assessment · Claude Sonnet 4.5",
    "cases.scoreExcellent": "Excellent clinical reasoning",
    "cases.scoreSolid": "Solid foundation, can be refined",
    "cases.scoreRepeat": "Review and try again",
    "cases.backToCase": "Back to the case",
    "cases.errLoad": "Couldn't load data",
    "cases.errGen": "Couldn't generate case",
    "cases.errAssess": "Couldn't assess choices",
    "cases.selectOne": "Select at least one test",

    // Chat
    "chat.eyebrow": "Claude Sonnet 4.5",
    "chat.title": "AI assistant",
    "chat.lead": "Clinical reasoning, differential diagnoses, and test support.",
    "chat.suggestionsTitle": "Suggestions to start with:",
    "chat.sug1": "What does a positive Spurling test mean and which structures can be involved?",
    "chat.sug2": "My patient has a positive Lachman and negative anterior drawer – how do you interpret that?",
    "chat.sug3": "Difference between Hawkins-Kennedy and Neer impingement?",
    "chat.sug4": "Which tests should I do first for suspected cauda equina?",
    "chat.input": "Write your clinical question…",
    "chat.thinking": "Thinking…",
    "chat.disclaimer": "AI-generated content — verify clinically. Does not replace physician assessment.",
    "chat.reset": "Reset",
    "chat.err": "The AI assistant couldn't respond right now",

    // Diagnose
    "dx.eyebrow": "Clinical reasoning",
    "dx.title1": "Differential diagnosis",
    "dx.titleAccent": "– built from findings.",
    "dx.lead": "Mark the tests that were positive for your patient. The AI (Claude Sonnet 4.5) summarizes likely diagnoses, suggests follow-up tests and red flags to rule out.",
    "dx.search": "Search test or region…",
    "dx.importFav": "Import {n} favorites",
    "dx.selected": "Selected positive tests",
    "dx.empty": "Mark tests on the left, or import from favorites.",
    "dx.notes": "History / notes (optional)",
    "dx.notesPh": "E.g.: 32-year-old runner, 3 weeks of medial knee pain after increased training…",
    "dx.run": "Generate differential diagnosis",
    "dx.result": "AI analysis (Claude Sonnet 4.5)",
    "dx.disclaimer": "The assessment is AI-generated and does not replace a clinical examination.",
    "dx.howTitle": "How it works",
    "dx.how": "The AI combines your positive findings with clinical evidence from the examination methodology and produces a ranked differential diagnosis. It also suggests relevant follow-up tests and red flags.",
    "dx.discuss": "Need to discuss a case? Open the AI assistant",
    "dx.errLoad": "Couldn't load tests",
    "dx.errRun": "The AI service is not available right now",
    "dx.selectFirst": "Select at least one positive test",
    "dx.noFav": "No favorites to import",
    "dx.added": "{n} favorites added",

    // Flashcards
    "flash.eyebrow": "Quick review",
    "flash.title": "Flashcards",
    "flash.lead": "Flip the cards to see execution and positive findings. Mark \"Knew\" or \"Need practice\".",
    "flash.region": "Region",
    "flash.count": "Number of cards",
    "flash.allRegions": "All regions",
    "flash.start": "Start flashcards",
    "flash.cardOf": "Card",
    "flash.knewCount": "{n} knew",
    "flash.reviewCount": "{n} to review",
    "flash.tapToReveal": "Tap to see the answer →",
    "flash.answer": "Answer",
    "flash.needPractice": "Need practice",
    "flash.knew": "Knew",
    "flash.summary": "{known} knew · {review} to review · {total} total",
    "flash.reviewThese": "Review these",
    "flash.newRound": "New round",
    "flash.redoReview": "Review {n} cards",
    "flash.noneAvail": "No cards available",
    "flash.errLoad": "Couldn't load cards",

    // Favorites
    "fav.eyebrow": "Saved",
    "fav.title": "Favorites",
    "fav.lead1": "Quick access to tests you've marked. Save with",
    "fav.lead2": "on any test.",
    "fav.clearAll": "Clear all",
    "fav.cleared": "Favorites cleared",
    "fav.emptyTitle": "No favorites yet",
    "fav.emptyLead": "Browse regions and save tests you want quick access to.",
    "fav.explore": "Explore regions",
    "fav.countLabel": "{n} favorite tests",
    "fav.useAsDx": "Use as positive findings in the AI diagnosis",
    "fav.toDx": "Send to differential diagnosis",
    "fav.errLoad": "Couldn't load favorites",

    // About
    "about.eyebrow": "About the app",
    "about.title1": "Clinical examination methodology",
    "about.titleAccent": "– digitized.",
    "about.lead": "The app summarizes manual medicine methodology from head to heel. Tests by region, focused on purpose, execution and positive findings. The AI assistant helps you interpret findings and reason about differential diagnoses.",
    "about.item1Title": "Structured methodology",
    "about.item1Text": "Standing → seated → lying. Inspection, active range of motion, palpation and provocation tests per region.",
    "about.item2Title": "Clinical reference",
    "about.item2Text": "Quick search and position filter. Ideal during practical exams and in the clinic.",
    "about.item3Title": "Safety",
    "about.item3Text": "The app is an educational tool. It never replaces a real physician's assessment — especially not in cases of red flags.",
    "about.sourceLabel": "Source",
    "about.sourceText": "Based on Clinical examination (Magee references).",

    // Support / Swish
    "sup.title": "Support the project",
    "sup.lead": "Manual Medicine is an independent educational tool. Every contribution helps cover AI costs and keep the app free for students.",
    "sup.otherAmount": "Or other amount",
    "sup.placeholder": "e.g. 150",
    "sup.scan": "Scan the QR code in the Swish app",
    "sup.openApp": "Open in Swish app",
    "sup.swishLabel": "Swish",
    "sup.thanks": "Thank you for your contribution! 💙",
    "sup.copied": "Swish number copied",
    "sup.copyFail": "Couldn't copy",
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(KEY) || "sv");
  useEffect(() => { localStorage.setItem(KEY, lang); }, [lang]);

  const t = useCallback((k, vars) => {
    let s = DICT[lang][k] ?? DICT.sv[k] ?? k;
    if (vars) Object.entries(vars).forEach(([key, v]) => { s = s.replace(`{${key}}`, v); });
    return s;
  }, [lang]);

  const toggle = useCallback(() => setLang((l) => (l === "sv" ? "en" : "sv")), []);

  const value = useMemo(() => ({ lang, setLang, t, toggle }), [lang, t, toggle]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useLang() {
  const ctx = useContext(LangCtx);
  if (!ctx) throw new Error("useLang must be inside LanguageProvider");
  return ctx;
}
